{
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "$id": "https://raw.githubusercontent.com/jwildfire/safety.viz/main/src/data/schema/nep-explorer.json",
  "title": "safety.viz nep-explorer data contract",
  "description": "Long-format laboratory data: one record per participant per measure per visit (NEP-DATA-001). Column names are supplied by the settings mapping. The nep-explorer reads the serum-creatinine records only, resolves each participant's baseline (an explicit baseline flag when configured, otherwise the earliest record), converts every record to mg/dL through the per-record unit table, and reduces each participant to one point: their maximum post-baseline fold change against their maximum post-baseline absolute change, staged on the KDIGO acute-kidney-injury criteria. Phase 1 requires only id, measure, value, unit and a way to find baseline — a deliberately small contract, so the scatter ports to a study carrying nothing but a chemistry panel. Records that cannot be plotted are counted and exported rather than dropped silently (NEP-DATA-005).",
  "type": "object",
  "required": ["data", "settings"],
  "properties": {
    "data": {
      "type": "array",
      "minItems": 1,
      "items": { "type": "object" },
      "description": "d3.csv()-style records; every row carries the participant, measure, result, unit and visit columns named in settings, one row per participant per measure per visit."
    },
    "settings": {
      "type": "object",
      "description": "Column mappings and rendering options; merged onto the module's DEFAULT_SETTINGS, so only overrides need to be supplied.",
      "required": ["id_col", "measure_col", "value_col"],
      "properties": {
        "id_col": {
          "type": "string",
          "default": "USUBJID",
          "description": "Participant identifier column; required in data. One point per participant, and the key the participantsSelected event carries (NEP-CFG-001, NEP-SCAT-004)."
        },
        "measure_col": {
          "type": "string",
          "default": "TEST",
          "description": "Column holding the measure name; required in data. Matched against measure_values.CREAT to select the creatinine records (NEP-CFG-008)."
        },
        "value_col": {
          "type": "string",
          "default": "STRESN",
          "description": "Column holding the numeric result; required in data. Missing or non-numeric results are dropped with a counted, exportable note (NEP-DATA-005)."
        },
        "unit_col": {
          "type": ["string", "null"],
          "default": "STRESU",
          "description": "Column holding the result unit, resolved PER RECORD against the units factor table. When any record's unit is absent or unrecognized the module refuses to guess: the fold axis stays (it is a ratio), and the absolute cut-line, the >= 4.0 mg/dL rule and the delta staging are suppressed with a note (NEP-UNIT-002, NEP-UNIT-003)."
        },
        "baseline_col": {
          "type": ["string", "null"],
          "default": null,
          "description": "Optional baseline-flag column (e.g. ABLFL). When supplied, the participant's first record whose value equals baseline_value is the baseline; otherwise, and for a participant with no flagged record, the earliest record is used — by study day, then visit number, then input order (NEP-DATA-001, NEP-DATA-002)."
        },
        "baseline_value": {
          "type": "string",
          "default": "Y",
          "description": "The value of baseline_col that marks the baseline record (NEP-CFG-002)."
        },
        "visit_col": {
          "type": "string",
          "default": "VISIT",
          "description": "Categorical visit column; the tooltip's \"maximum at visit\" line and the baseline fallback's ordering (NEP-SCAT-003)."
        },
        "visitn_col": {
          "type": ["string", "null"],
          "default": "VISITNUM",
          "description": "Optional numeric visit column ordering the records; falls back to input order when absent."
        },
        "studyday_col": {
          "type": ["string", "null"],
          "default": "DY",
          "description": "Optional numeric study-day column: the baseline fallback's first sort key and the tooltip's \"maximum on study day\" line. Absent from the vendored demo extract, so the line degrades silently rather than rendering blank (NEP-SCAT-003)."
        },
        "measure_values": {
          "type": "object",
          "default": { "CREAT": "Creatinine" },
          "description": "Map of the short measure key to this data's full measure name. Phase 1 reads CREAT only; the map grows to the profile panel in Phase 2 (NEP-CFG-008)."
        },
        "arm_col": {
          "type": ["string", "null"],
          "default": "ARM",
          "description": "Treatment-arm column carried on each point for the tooltip; ignored when absent from the data."
        },
        "stages": {
          "type": "object",
          "description": "The staging cut-points, defaulting to the KDIGO acute-kidney-injury criteria (NEP-CFG-003, NEP-STAGE-001..004). Supplied members merge onto the defaults.",
          "properties": {
            "fold": {
              "type": "array",
              "items": { "type": "number" },
              "minItems": 3,
              "maxItems": 3,
              "default": [1.5, 2, 3],
              "description": "The ascending fold-change (value / baseline) cut-points for Stages 1, 2 and 3. Sorted ascending on merge: the R source paints its chart from a descending ladder and stages its table from an ascending one, and the two disagree."
            },
            "delta": {
              "type": "number",
              "default": 0.3,
              "description": "The single absolute-change cut-point KDIGO defines (mg/dL). It produces Stage 1 only — there is no KDIGO Stage 2 or 3 on absolute change (NEP-STAGE-002)."
            },
            "absolute": {
              "type": "number",
              "default": 4,
              "description": "The Stage-3 rule on the VALUE reached (mg/dL). A property of the participant, not a region of the plane, so it is drawn as a mark property rather than a zone (NEP-STAGE-003)."
            }
          }
        },
        "units": {
          "type": "object",
          "description": "The mg/dL contract (NEP-UNIT-002). The fold axis is a ratio and unit-free; the absolute axis and both absolute cut-points are not.",
          "properties": {
            "target": {
              "type": "string",
              "default": "mg/dL",
              "description": "The unit the absolute axis and the cut-points are expressed in."
            },
            "factors": {
              "type": "object",
              "additionalProperties": { "type": "number" },
              "default": { "mg/dl": 1, "umol/l": 0.011312217194570135 },
              "description": "Multiplier from each source unit to the target. Keys are normalized on merge — trimmed, lower-cased, with µ/μ/u folded — so one entry covers every spelling and casing. Sponsor-specific factors vary, which nepExplorer warns about itself, so this is a setting rather than a constant (NEP-UNIT-001)."
            }
          }
        },
        "filters": {
          "$ref": "#/$defs/filterList",
          "description": "Optional filter columns rendered as controls (NEP-CFG-006). Each entry is a column name or a { value_col, label, start, all, multiple } spec: `start` is the value the filter opens on, `all` (default true) is whether an \"All\" option is offered, and `multiple` (default false) makes it a checkbox multiselect."
        },
        "details": {
          "oneOf": [{ "$ref": "#/$defs/fieldList" }, { "type": "null" }],
          "default": null,
          "description": "Participant-detail columns shown with the selected participant; null defaults to the participant ID plus the filter columns."
        },
        "zone_labels": {
          "type": "string",
          "enum": ["shown", "hidden"],
          "default": "shown",
          "description": "Whether the scatter draws the stage-zone labels (NEP-ZONE-004)."
        }
      }
    }
  },
  "$defs": {
    "fieldList": {
      "type": "array",
      "items": {
        "anyOf": [
          { "type": "string" },
          {
            "type": "object",
            "required": ["value_col"],
            "properties": {
              "value_col": { "type": "string" },
              "label": { "type": "string" }
            }
          }
        ]
      }
    },
    "filterList": {
      "type": "array",
      "items": {
        "anyOf": [
          { "type": "string" },
          {
            "type": "object",
            "required": ["value_col"],
            "properties": {
              "value_col": { "type": "string" },
              "label": { "type": "string" },
              "start": {
                "type": ["string", "number", "boolean", "array", "null"],
                "default": null,
                "description": "The value the filter opens on; an array of values for a `multiple` filter. 0 and false are real values, not an absent start. A value the data lacks is dropped with a console warning."
              },
              "all": {
                "type": "boolean",
                "default": true,
                "description": "Whether the filter offers an \"All\" option. false removes it, and the filter then always holds one value: the first, unless `start` names another."
              },
              "multiple": {
                "type": "boolean",
                "default": false,
                "description": "Render a checkbox multiselect instead of a dropdown; the rows shown are those matching any selected value."
              }
            }
          }
        ]
      }
    }
  }
}
